import { useHead } from '@unhead/react';
import { Flex } from 'antd';
import invariant from 'tiny-invariant';
import { useTranslation } from 'translations/hooks';

import { useAppContext } from 'business/contextProviders/useAppContext';
import { SingleCardPageWithFilterCardLayout } from 'business/layout/single-card-page-with-filter-card';
import { SingleGraphReferenceContextProvider } from 'business/providers/graph-reference';
import { MobileActivityAnalyze } from 'business/shift-report/report-analyze/pages/activity-analyze/mobile';
import Routes from 'config/routes';
import { useMediaType } from 'technical/media/hooks';
import { PreviousPageButton } from 'ui/button/previous-page-button';
import { FilterCard } from 'ui/filter-card';

import { useActivityTree } from './components/aside-activity-tree/use-activity-tree';
import ReportActivityAnalyzeGraphContainer from './components/display-container';
import ReportActivityAnalyzeFilters from './components/filters';
import ReportActivityAnalyzeHeader from './components/header';
import ReportActivityAnalyzePrintHeader from './components/print-header';
import { ReportActivityAnalyzeFiltersProvider } from './providers/filters';
import { ActivityAnalyseLegendFilterProvider } from './providers/legend-filters';

const ActivityAnalyze = () => {
  const { t } = useTranslation();
  const { isMobile } = useMediaType();
  const { currentConstructionSiteId } = useAppContext();

  invariant(currentConstructionSiteId, 'No constructionSiteId');
  useHead({ title: t('user.menu.sections.reporting.title') });

  const activityTreeMetadata = useActivityTree({
    constructionSiteId: currentConstructionSiteId,
  });

  if (isMobile) {
    return (
      <MobileActivityAnalyze
        constructionSiteId={currentConstructionSiteId}
        activityTreeMetadata={activityTreeMetadata}
      />
    );
  }

  return (
    <SingleGraphReferenceContextProvider>
      <ReportActivityAnalyzeFiltersProvider
        constructionSiteId={currentConstructionSiteId}
        allActivitiesChecked={activityTreeMetadata.allActivitiesChecked}
      >
        <ActivityAnalyseLegendFilterProvider>
          <SingleCardPageWithFilterCardLayout
            title={t('pages.manager.taskAnalyzer.title')}
            header={<ReportActivityAnalyzeHeader />}
            footer={
              <Flex justify="center">
                <PreviousPageButton
                  to={Routes.ReportDashboardAnalyze}
                  label="common.to_main_page"
                />
              </Flex>
            }
            printHeader={<ReportActivityAnalyzePrintHeader />}
            filters={
              <FilterCard>
                <ReportActivityAnalyzeFilters />
              </FilterCard>
            }
          >
            <ReportActivityAnalyzeGraphContainer
              activityTreeMetadata={activityTreeMetadata}
            />
          </SingleCardPageWithFilterCardLayout>
        </ActivityAnalyseLegendFilterProvider>
      </ReportActivityAnalyzeFiltersProvider>
    </SingleGraphReferenceContextProvider>
  );
};

export default ActivityAnalyze;
